/**
 * Charte de la nouvelle interface, pour l'aperçu administrateur seulement (route #/v2/charte,
 * déclarée par AppV2 quand uxV2Source() === 'preview') : montre les jetons --mmv2-* calculés pour
 * CE site et les composants de base de la maquette /msc, pour vérifier une marque d'un coup d'œil
 * (couleurs, contrastes, rayons, police) avant d'activer la v2 pour tous.
 */
import { Button, HStack } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { contrastRatio, textOnColor, WHITE } from '../../lib/color';
import { cssThemeTokenReader, readSettings, resolveTokens, type V2Tokens } from '../../theme/tokens';
import { Icon } from '../Class/components/Icon';
import { IconRight } from '../Planning/icons';

const ratio = (a: string, b: string) => contrastRatio(a, b).toFixed(2).replace('.', ',');

function Swatch({ name, value, on }: { name: string; value: string; on?: string }) {
  return (
    <div className="mmv2-card" style={{ overflow: 'hidden' }}>
      {/* Interligne « normal » (contrôle d'une ligne) : « Aa x:1 » centré au pixel sur le bloc (1,8 à 2 px trop haut). */}
      <div style={{ height: 56, background: value, color: on, display: 'grid', placeItems: 'center', fontWeight: 600, lineHeight: 'normal' }}>
        {on ? `Aa ${ratio(on, value)}:1` : null}
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div className="mmv2-label">{name}</div>
        <div className="mmv2-tiny mmv2-muted" style={{ overflowWrap: 'anywhere' }}>
          {on ? `${on} sur ${value}` : value}
        </div>
      </div>
    </div>
  );
}

/**
 * Tableau plus large que sa carte (390 px : 586 px pour 324) : fondu au bord droit tant qu'il reste des colonnes à
 * faire défiler (revue du 05/10 : Contraste et Aperçu cachés sans indice).
 */
function ScrollCard({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, []);
  const mask = more ? 'linear-gradient(to right, #000 calc(100% - 36px), transparent)' : undefined;
  return (
    <div ref={ref} className="mmv2-card" style={{ overflowX: 'auto', maskImage: mask, WebkitMaskImage: mask }}>
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginTop: 40 }}>
      <h2 className="mmv2-h2" style={{ marginBottom: 16 }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Couleurs de référence des 3 marques (réglage « Couleur » des sites, relevé du 04/10), en plus de celles du site. */
const REFERENCE_COLORS: Array<[string, string]> = [
  ['Dynamo (thème monday-2026)', '#e0141c'],
  ['Dynamo (logo, sites Uncode)', '#ff2021'],
  ['RIISE', '#7d301c'],
  ['Punch (orange)', '#ff5717'],
  ['Punch (bleu)', '#263fff'],
];

const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 };
const row = { display: 'flex', flexWrap: 'wrap' as const, gap: 12, alignItems: 'center' };

export function Charte() {
  const settings = useMemo(() => readSettings(), []);
  const theme = useMemo(() => cssThemeTokenReader(), []);
  const t: V2Tokens = useMemo(() => resolveTokens(settings, theme), [settings, theme]);
  const [chip, setChip] = useState('Toutes');
  // Pile de police réellement posée sur #mindbody-app (alias aux métriques corrigées, theme/fontAlias.ts).
  const [fontStack, setFontStack] = useState('');
  useEffect(() => {
    const read = () =>
      setFontStack(getComputedStyle(document.querySelector('#mindbody-app') ?? document.body).getPropertyValue('--mmv2-font').trim());
    read();
    const timer = window.setTimeout(read, 1500);
    return () => window.clearTimeout(timer);
  }, []);
  const brandRows: Array<[string, string]> = [
    ['Marque de ce site', t.brand],
    ["Action de ce site", t.action],
    ...((typeof mondayMindbody !== 'undefined' ? mondayMindbody?.uxV2?.brands : undefined) ?? [])
      .filter((b) => !b.isCurrent)
      .map((b): [string, string] => [`${b.name} (réglage du BO)`, b.color]),
    ...REFERENCE_COLORS,
  ];
  const [seg, setSeg] = useState('Packs');

  const sources: Array<[string, string]> = [
    ['Couleur (réglage)', settings.color || '—'],
    ["Couleur d’action (réglage v2)", settings.actionColor || '—'],
    ['Arrondi des boutons (réglage)', settings.buttonBorderRadius !== undefined ? String(settings.buttonBorderRadius) : '—'],
    ['Arrondi des cards (réglage)', settings.cardBorderRadius !== undefined ? String(settings.cardBorderRadius) : '—'],
    ['Police (réglage)', settings.fontFamily || '—'],
    ['Jetons du thème (--m-brand-700)', theme('--m-brand-700') || 'absents : réglages du plugin'],
    ['Police (pile appliquée)', fontStack || t.font],
  ];

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', padding: '40px 16px 80px', background: t.canvas }}>
      <div className="mmv2-kicker mmv2-kicker--brand">Nouvelle interface · aperçu administrateur</div>
      <h1 className="mmv2-h1" style={{ marginTop: 8 }}>
        Charte
      </h1>
      <p className="mmv2-lede" style={{ marginTop: 12, maxWidth: '60ch' }}>
        Jetons calculés pour ce site : thème du site d’abord, puis réglages du plugin, puis valeurs de la maquette.
      </p>

      <Section title="Sources">
        <div className="mmv2-card mmv2-card--pad">
          {sources.map(([k, v]) => (
            // Libellé et valeur côte à côte, l'un sous l'autre sous 640 px (ux-v2.css, .mmv2-charte-source).
            <div key={k} className="mmv2-charte-source">
              <span className="mmv2-muted mmv2-small">{k}</span>
              {/* Coupure entre les familles de la pile (après chaque virgule), jamais au milieu d'un mot. */}
              <b className="mmv2-small mmv2-charte-source__value">
                {String(v)
                  .split(/,\s*/)
                  .map((part, i, all) => (
                    <span key={i} style={{ whiteSpace: 'nowrap' }}>
                      {part}
                      {i < all.length - 1 ? ', ' : ''}
                      {i < all.length - 1 ? <wbr /> : null}
                    </span>
                  ))}
              </b>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Couleurs">
        <div style={grid}>
          <Swatch name="Marque" value={t.brand} />
          <Swatch name="Marque (fond sous texte)" value={t.brandSurface} on={t.brandOn} />
          <Swatch name="Marque survol" value={t.brandHover} on={t.brandOn} />
          <Swatch name="Action" value={t.action} />
          <Swatch name="Action (fond sous texte)" value={t.actionSurface} on={t.actionOn} />
          <Swatch name="Action survol" value={t.actionHover} on={t.actionOn} />
          <Swatch name="Texte marque" value={t.paper} on={t.brandText} />
          <Swatch name="Texte action" value={t.action50} on={t.actionText} />
          <Swatch name="Marque 50" value={t.brand50} />
          <Swatch name="Marque 100" value={t.brand100} />
          <Swatch name="Encre" value={t.ink} on={t.paper} />
          <Swatch name="Encre 2" value={t.canvas} on={t.ink2} />
          <Swatch name="Encre 3" value={t.canvas} on={t.ink3} />
          <Swatch name="Encre 4" value={t.ink4} />
          <Swatch name="Papier" value={t.paper} />
          <Swatch name="Canvas" value={t.canvas} />
          <Swatch name="Creux" value={t.sunken} />
          <Swatch name="Vert" value={t.green50} on={t.greenText} />
          <Swatch name="Ambre" value={t.amber50} on={t.amberText} />
          <Swatch name="Rouge" value={t.red50} on={t.redText} />
        </div>
      </Section>

      <Section title="Texte sur la couleur des marques">
        <p className="mmv2-small mmv2-muted" style={{ marginBottom: 12, maxWidth: '80ch' }}>
          Convention des marques, garantie AA : blanc sur une couleur qui le porte déjà à 3:1 (Dynamo, RIISE et Punch
          posent du blanc sur leurs boutons), la surface étant foncée au minimum dans sa teinte jusqu’à 4,5:1 ; encre
          sur une couleur claire. La couleur elle-même (liserés, pastilles) ne change pas.
        </p>
        {/*
         * Interligne « normal » (règle des contrôles d'une ligne, ux-v2.css) et cellules centrées verticalement : valeur,
         * pastille, textes voisins et aperçu « Réserver » sur une même ligne au pixel (revue du 05/10 : texte 1,2 px
         * au-dessus de la pastille). Chaque cellule sur une seule ligne (le tableau défile déjà) : à 390, « FOND / SOUS /
         * TEXTE » et « Dynamo / (thème / monday- / 2026) » passaient sur 3 et 4 lignes serrées (revue du 05/10).
         */}
        <ScrollCard>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, lineHeight: 'normal', whiteSpace: 'nowrap' }}>
            <thead>
              <tr className="mmv2-label" style={{ textAlign: 'left' }}>
                {['Couleur', 'Valeur', 'Fond sous texte', 'Texte', 'Contraste', 'Aperçu'].map((h) => (
                  <th key={h} style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {brandRows.map(([name, color]) => {
                const on = textOnColor(color, t.ink, WHITE);
                return (
                  <tr key={`${name}-${color}`} style={{ borderTop: `1px solid ${t.line}` }}>
                    <td style={{ padding: '10px 14px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>{name}</td>
                    <td style={{ padding: '10px 14px', verticalAlign: 'middle' }}>
                      {/*
                       * Flex de bloc, pas inline-flex : posé dans la ligne, le conteneur prenait pour ligne de base le bas
                       * de la pastille (sans texte) et descendait la valeur de 1 px sous le texte des cellules voisines.
                       */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, lineHeight: 'normal' }}>
                        <span className="mmv2-chip__dot" style={{ ['--mmv2-dot' as string]: color }} />
                        {color}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {on.surface === color.toLowerCase() ? `${on.surface} (inchangée)` : on.surface}
                    </td>
                    <td style={{ padding: '10px 14px', verticalAlign: 'middle' }}>{on.text === WHITE ? 'blanc' : `encre ${on.text}`}</td>
                    <td style={{ padding: '10px 14px', verticalAlign: 'middle' }}>{`${ratio(on.text, on.surface)}:1`}</td>
                    <td style={{ padding: '6px 14px', verticalAlign: 'middle' }}>
                      <span
                        className="mmv2-btn mmv2-btn--sm"
                        style={{ background: on.surface, color: on.text, cursor: 'default', verticalAlign: 'middle' }}
                      >
                        Réserver
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollCard>
      </Section>

      <Section title="Boutons">
        <div style={row}>
          <button type="button" className="mmv2-btn mmv2-btn--cta">
            Réserver
          </button>
          <button type="button" className="mmv2-btn mmv2-btn--primary">
            Confirmer mon spot
          </button>
          <button type="button" className="mmv2-btn mmv2-btn--dark">
            Liste d’attente
          </button>
          <button type="button" className="mmv2-btn mmv2-btn--ghost">
            Se connecter
          </button>
          <button type="button" className="mmv2-btn mmv2-btn--quiet">
            Annuler
          </button>
          <button type="button" className="mmv2-btn mmv2-btn--cta" disabled>
            Désactivé
          </button>
        </div>
        <div style={{ ...row, marginTop: 12 }}>
          <button type="button" className="mmv2-btn mmv2-btn--cta mmv2-btn--sm">
            Petit
          </button>
          <button type="button" className="mmv2-btn mmv2-btn--cta">
            Moyen
          </button>
          <button type="button" className="mmv2-btn mmv2-btn--cta mmv2-btn--lg">
            Grand
          </button>
          <button type="button" className="mmv2-iconbtn" aria-label="Semaine suivante">
            <IconRight />
          </button>
          <button type="button" className="mmv2-linkish">
            Effacer les filtres
          </button>
        </div>
        <HStack mt="12px" spacing="12px" flexWrap="wrap">
          <Button variant="mmv2Cta" size="mmv2Md">
            Chakra Cta
          </Button>
          <Button variant="mmv2Primary" size="mmv2Md">
            Chakra Primary
          </Button>
          <Button variant="mmv2Dark" size="mmv2Sm">
            Chakra Dark
          </Button>
          <Button variant="mmv2Ghost" size="mmv2Sm">
            Chakra Ghost
          </Button>
          <Button variant="mmv2Quiet" size="mmv2Sm">
            Chakra Quiet
          </Button>
          <Button variant="mmv2Cta" size="mmv2Lg" isLoading>
            Chargement
          </Button>
        </HStack>
      </Section>

      <Section title="Étiquettes">
        <div className="mmv2-chips">
          {['Toutes', 'Marque du site', 'Autre marque'].map((label, i) => (
            <button
              key={label}
              type="button"
              className="mmv2-chip"
              aria-pressed={chip === label}
              onClick={() => setChip(label)}
            >
              {i > 0 ? <span className="mmv2-chip__dot" style={{ ['--mmv2-dot' as string]: i === 1 ? t.brand : t.ink3 }} /> : null}
              {label}
            </button>
          ))}
        </div>
        <div style={{ ...row, marginTop: 14, gap: 8 }}>
          <span className="mmv2-tag mmv2-tag--action">Réserver</span>
          <span className="mmv2-tag mmv2-tag--amber">Liste d’attente</span>
          <span className="mmv2-tag">Complet</span>
          <span className="mmv2-tag mmv2-tag--green">Réservé</span>
          <span className="mmv2-tag">Terminé</span>
          <span className="mmv2-tag mmv2-tag--red">Annulation tardive</span>
          <span className="mmv2-tag mmv2-tag--brand">Marque</span>
          <span className="mmv2-tag mmv2-tag--solid">Encre</span>
        </div>
      </Section>

      <Section title="Cartes, encadrés, champs">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          <div className="mmv2-card mmv2-card--pad">
            <div className="mmv2-kicker">Votre réservation</div>
            <h3 className="mmv2-h3" style={{ marginTop: 8 }}>
              Carte
            </h3>
            <p className="mmv2-small mmv2-muted" style={{ marginTop: 6 }}>
              Rayon des cartes : {t.rCard}. Rayon des pilules : {t.rPill}.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Comme les encadrés réels : icône et texte (information neutre en gris depuis le 04/10). */}
            <div className="mmv2-notice">
              <Icon name="info" />
              <span>
                <b>Encadré.</b> Information neutre (gris).
              </span>
            </div>
            <div className="mmv2-notice mmv2-notice--warm">
              <Icon name="alert" />
              <span>
                <b>Encadré d’action.</b> À la couleur d’action.
              </span>
            </div>
          </div>
          <div className="mmv2-card mmv2-card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label className="mmv2-field">
              <span className="mmv2-label">Champ</span>
              <input className="mmv2-input" placeholder="Texte d’exemple" />
            </label>
            <div className="mmv2-seg" role="group" aria-label="Segment">
              {['Packs', 'Abonnements'].map((label) => (
                <button key={label} type="button" aria-pressed={seg === label} onClick={() => setSeg(label)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
